import type { SetupShippingRateDraft } from '@/components/setup/experience/lib/setupShippingRateTypes';
import type {
  CreateShippingRateInput,
  ShippingRateItem,
  UpdateShippingRateInput,
} from '@/lib/api/shippingRates';
import { minorUnitsToCurrencyJs, moneyStringOrNumberToMinorUnits } from '@/lib/money';
import { createZeroCurrencyOptions } from '@/lib/utils/currency';

/** Fields the setup wizard owns on a shipping rate — shared by the create and update payloads. */
function buildSetupShippingRateData(
  currency: string,
  rate: SetupShippingRateDraft,
  // On update, send '' so a removed logo is actually cleared — the use case's `.set()` skips
  // `undefined`, so omitting it would silently keep the old icon. On create we omit it (column null).
  { keepEmptyIcon = false }: { keepEmptyIcon?: boolean } = {},
) {
  const isFree = rate.isFree;
  const amount = isFree
    ? createZeroCurrencyOptions([currency])
    : {
        [currency]: {
          amount: moneyStringOrNumberToMinorUnits(rate.priceMajor, currency),
          rate: 1,
          lock: false,
          date: new Date().toISOString(),
        },
      };

  const icon = rate.icon?.trim() ?? '';

  return {
    shippingRateName: rate.shippingRateName.trim(),
    icon: keepEmptyIcon ? icon : icon || undefined,
    amount,
    isFree,
    // Echoed back, never decided here — the wizard has no pickup-point UI, and the update payload
    // is not partial, so a hardcoded `false` wiped the flag on existing rates (#TAG-1779).
    isPickupPoint: rate.isPickupPoint,
    // Numeric ISO 3166-1 codes ("840") — checkout eligibility matches this format; [] = worldwide.
    countryCodes: rate.allCountries ? [] : rate.countryCodes,
  };
}

export function buildSetupShippingRateInput(
  storeId: string,
  currency: string,
  rate: SetupShippingRateDraft,
  highlighted: boolean,
): CreateShippingRateInput {
  return {
    storeId,
    ...buildSetupShippingRateData(currency, rate),
    description: undefined,
    highlighted,
    shippingRateMinAmount: null,
    shippingRateMaxAmount: null,
    shippingRateMinWeight: null,
    shippingRateMaxWeight: null,
    pickupPointCarriers: null,
  };
}

/**
 * Update payload for an existing rate. Only the wizard-owned fields are sent; cosmetic fields
 * (description, highlighted, min/max, weight) are omitted so the use case's `.set()` leaves them
 * untouched. `icon` IS sent (incl. '') so removing a logo persists rather than silently reverting.
 */
export function buildSetupShippingRateUpdateInput(
  id: string,
  currency: string,
  rate: SetupShippingRateDraft,
): UpdateShippingRateInput {
  return { id, data: buildSetupShippingRateData(currency, rate, { keepEmptyIcon: true }) };
}

/** Inverse of {@link buildSetupShippingRateInput}: a persisted rate → an editable form draft. */
export function mapSetupShippingRateToDraft(
  rate: ShippingRateItem,
  currency: string,
): SetupShippingRateDraft {
  const countryCodes = rate.countryCodes ?? [];
  const isFree = Boolean(rate.isFree);

  let priceMajor = '';
  if (!isFree) {
    const options = rate.amount ?? {};
    const priceCurrency = options[currency] ? currency : Object.keys(options)[0];
    const amountMinor = priceCurrency ? options[priceCurrency]?.amount : undefined;
    if (amountMinor != null) {
      priceMajor = String(minorUnitsToCurrencyJs(amountMinor, priceCurrency ?? currency).value);
    }
  }

  return {
    id: rate.id,
    shippingRateName: rate.shippingRateName?.trim() ?? '',
    icon: rate.icon ?? '',
    isFree,
    priceMajor,
    allCountries: countryCodes.length === 0,
    countryCodes,
    isPickupPoint: Boolean(rate.isPickupPoint),
  };
}

/** True when two drafts describe the same rate — used to skip no-op updates on submit. */
export function isSameSetupShippingDraft(a: SetupShippingRateDraft, b: SetupShippingRateDraft): boolean {
  if (a.shippingRateName.trim() !== b.shippingRateName.trim()) return false;
  if ((a.icon ?? '').trim() !== (b.icon ?? '').trim()) return false;
  if (a.isFree !== b.isFree) return false;
  if (!a.isFree && a.priceMajor.trim() !== b.priceMajor.trim()) return false;
  if (a.allCountries !== b.allCountries) return false;

  if (!a.allCountries) {
    const sortedA = [...a.countryCodes].sort();
    const sortedB = [...b.countryCodes].sort();
    if (sortedA.length !== sortedB.length) return false;
    if (sortedA.some((code, index) => code !== sortedB[index])) return false;
  }

  return true;
}
